import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';
import PhoneAuth from '../../components/auth/PhoneAuth';

const LoginPage = () => {
  const [loginMethod, setLoginMethod] = useState('email');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      navigate(from, { replace: true });
    } catch (err) {
      setError('Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  // Твои премиальные стили Zootel
  const inputStyle = "w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-[14px] text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-[#F65814] focus:ring-4 focus:ring-[#F65814]/5 transition-all duration-200 outline-none font-medium";
  const labelStyle = "block text-[10px] font-black uppercase tracking-[1.5px] text-slate-400 mb-1.5 ml-1 cursor-pointer";
  const mainButtonStyle = "w-full bg-[#0A1128] text-white py-4 rounded-[18px] font-black uppercase tracking-[2px] text-[12px] flex items-center justify-center gap-2 hover:bg-[#152042] transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center py-6 px-4 font-['Inter']">
      <motion.div 
        initial={{ opacity: 0, y: 10 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="w-full max-w-[400px]"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-[900] text-[#0A1128] tracking-tight uppercase">
            Welcome <span className="text-[#F65814]">Back</span>
          </h2>
          <p className="text-slate-500 text-[13px] mt-1 font-medium">
            New here? <Link to="/register" className="text-[#F65814] font-bold hover:underline underline-offset-4">Create account</Link>
          </p>
        </div>

        <div className="bg-white p-6 md:p-8 rounded-[32px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.05)] border border-slate-100">
          {/* Переключатель методов */}
          <div className="bg-slate-100 p-1 rounded-[16px] flex mb-6 relative">
            <motion.div 
              className="absolute bg-white rounded-[12px] shadow-sm inset-y-1 w-[calc(50%-4px)] z-0"
              animate={{ x: loginMethod === 'email' ? 0 : '100%' }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
            />
            <button 
              type="button" 
              onClick={() => setLoginMethod('email')} 
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-[12px] text-[10px] font-black uppercase tracking-wider z-10 transition-colors ${loginMethod === 'email' ? 'text-[#0A1128]' : 'text-slate-400'}`}
            >
              <EnvelopeIcon className="h-4 w-4" /> Email
            </button>
            <button 
              type="button" 
              onClick={() => setLoginMethod('phone')} 
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-[12px] text-[10px] font-black uppercase tracking-wider z-10 transition-colors ${loginMethod === 'phone' ? 'text-[#0A1128]' : 'text-slate-400'}`}
            >
              <PhoneIcon className="h-4 w-4" /> Phone
            </button>
          </div>

          <AnimatePresence mode="wait">
            {loginMethod === 'email' ? (
              <motion.form 
                key="email-form" 
                initial={{ opacity: 0, x: -10 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: 10 }} 
                onSubmit={handleSubmit} 
                className="space-y-4"
              >
                {error && (
                  <motion.div 
                    initial={{ scale: 0.9 }} 
                    animate={{ scale: 1 }} 
                    className="bg-red-50 text-[#F65814] text-[11px] font-bold p-3 rounded-[12px] border border-red-100 text-center uppercase tracking-wider"
                  >
                    {error}
                  </motion.div>
                )}
                
                <div>
                  <label htmlFor="email" className={labelStyle}>Email Address</label>
                  <input 
                    id="email"
                    name="email" 
                    type="email" 
                    required 
                    value={formData.email} 
                    onChange={handleChange} 
                    className={inputStyle} 
                    placeholder="name@mail.com" 
                  />
                </div>
                <div className="relative">
  <div className="flex justify-between items-center mb-1">
    <label htmlFor="password" className={labelStyle}>
      Password
    </label>

    <Link
      to="/forgot-password"
      className="text-[9px] font-black text-[#F65814] uppercase tracking-widest hover:text-[#0A1128] transition-colors"
    >
      Forgot?
    </Link>
  </div>

  <div className="relative">
    <input
      id="password"
      name="password"
      type={showPassword ? "text" : "password"}
      required
      value={formData.password}
      onChange={handleChange}
      className={`${inputStyle} pr-12`}
    />

    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="absolute right-0 top-1/2 -translate-y-1/2 h-full w-22 flex items-center justify-center text-slate-400 hover:text-[#0A1128] transition-colors"
    >
      {showPassword ? (
        <EyeSlashIcon className="h-4 w-4" />
      ) : (
        <EyeIcon className="h-4 w-4" />
      )}
    </button>
  </div>
</div>

                <button type="submit" disabled={loading} className={mainButtonStyle}>
                  {loading ? "Processing..." : "Sign In"}
                  {!loading && <ArrowRightIcon className="h-4 w-4" />}
                </button>
              </motion.form>
            ) : (
              <motion.div 
                key="phone-form" 
                initial={{ opacity: 0, x: 10 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -10 }}
              >
                <PhoneAuth 
                  onSuccess={() => navigate(from)} 
                  inputClassName={inputStyle} 
                  buttonClassName={mainButtonStyle} 
                  labelClassName={labelStyle}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100" /></div>
            <div className="relative flex justify-center text-[9px] uppercase font-black tracking-[2.5px] text-slate-300">
              <span className="px-4 bg-white">Connect</span>
            </div>
          </div>

          <button 
            type="button" 
            disabled={loading}
            onClick={handleGoogleLogin} 
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-slate-200 rounded-[18px] bg-white text-[12px] font-bold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm active:scale-[0.99] disabled:opacity-50"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google Account
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;